import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Elevation from '@segment/matcha/components/Elevation'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import privacyPortalPng2x from './assets/PrivacyPortal@2x.png'
import privacyPortalWebp from './assets/PrivacyPortal.webp'
import privacyPortalWebp2x from './assets/PrivacyPortal@2x.webp'
import privacyPortalWebp3x from './assets/PrivacyPortal@3x.webp'

const copy = {
  heading: `Take a proactive approach to privacy with the Privacy Portal`,
}

const features = [
  {
    heading: `Automate data discovery and classification`,
    description: `Automatically detect and classify personal information in real time instead of wasting months manually creating an inventory.`,
  },
  {
    heading: `Manage your risk exposure`,
    description: `Set rules to proactively block, hash, or route customer data to comply with your company’s privacy requirements.`,
  },
]

const link = {
  text: `Learn more`,
  url: `/product/privacy-portal/`,
}

const PrivacyPortal = () => {
  const pageConfig = usePageConfig()
  return (
    <SectionBase variant="2Col" reverse={true}>
      <SectionCopy>
        <CopyTextBase>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description tag="div">
            <ColumnTrackWrapper>
              <ColumnTrack columns={1} overrides={{ sm: 2, lg: 1 }}>
                {features.map((feature) => (
                  <FeatureBoxBase key={feature.heading} alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{feature.heading}</FeatureBox.Title>
                      <FeatureBox.Text>{feature.description}</FeatureBox.Text>
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}
              </ColumnTrack>
            </ColumnTrackWrapper>
          </CopyText.Description>
          <CopyText.Link href={getAbsoluteUrl(link.url, pageConfig)}>{link.text}</CopyText.Link>
        </CopyTextBase>
      </SectionCopy>
      <Section.Contents>
        <Elevation elevation={1} type="dropShadow">
          <picture>
            <source
              type="image/webp"
              srcSet={`${privacyPortalWebp}, ${privacyPortalWebp2x} 2x, ${privacyPortalWebp3x} 3x`}
            />
            <StyledImage
              src={privacyPortalPng2x}
              alt="Illustration: Privacy portal"
              width="620"
              height="669"
              loading="lazy"
            />
          </picture>
        </Elevation>
      </Section.Contents>
    </SectionBase>
  )
}

const StyledImage = styled(Image)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-left: 60px;
    padding-right: 30px;
  }
`

const SectionCopy = styled(Section.Copy)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-top: 30px;
    padding-bottom: 80px;
    justify-self: end;
    align-self: start;
    width: 85%;
  }
`

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

export default PrivacyPortal
